import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import Seo from "../components/Seo";
import Dimension from "../components/Dimension";
import PageHead from "../components/PageHead";

import { LT_PROJECTS } from "../data";

const CATEGORIES = [
  { id: "ALL", key: "projects.filters.all" },
  { id: "BIM", key: "projects.filters.bim" },
  { id: "LCA", key: "projects.filters.lca" },
  { id: "Software", key: "projects.filters.software" },
];

function Projects() {
  const { t } = useTranslation();
  const [filter, setFilter] = useState("ALL");
  const [expanded, setExpanded] = useState(null);

  // The copy for each project lives in the language bundles, keyed by id;
  // the English in LT_PROJECTS is the fallback.
  const translated = t("projects.projectsList", { returnObjects: true });
  const copyById = new Map(
    (Array.isArray(translated) ? translated : []).map((p) => [p.id, p]),
  );
  const withCopy = (p) => ({ ...p, ...(copyById.get(p.id) || {}) });

  const list = LT_PROJECTS.filter(
    (p) => filter === "ALL" || p.category === filter,
  ).map(withCopy);
  const categoryLabel = (id) =>
    t(CATEGORIES.find((c) => c.id === id)?.key ?? "projects.filters.all");

  return (
    <div className="container">
      <Seo page="projects" path="/projects" />

      <PageHead title={t("projects.heroTitle")} lede={t("projects.heroDesc")} />

      <div className="band-tight">
        <Dimension label={t("dimension.projects", { n: list.length })} />
      </div>

      <div className="filters">
        <span className="label">{t("projects.filterLabel")}</span>
        {CATEGORIES.map((f) => (
          <button
            key={f.id}
            onClick={() => {
              setFilter(f.id);
              setExpanded(null);
            }}
            aria-pressed={filter === f.id}
            className={filter === f.id ? "active" : ""}
          >
            {t(f.key)}
            <span className="n">
              {f.id === "ALL"
                ? LT_PROJECTS.length
                : LT_PROJECTS.filter((p) => p.category === f.id).length}
            </span>
          </button>
        ))}
      </div>

      <table className="schedule schedule-filtered">
        <thead>
          <tr>
            <th className="c-idx">{t("projects.colNo")}</th>
            <th>{t("projects.colTitle")}</th>
            <th className="hide-sm">{t("projects.colDesc")}</th>
            <th>{t("projects.colCategory")}</th>
            <th className="c-toggle" />
          </tr>
        </thead>
        <tbody>
          {list.map((p, i) => {
            const open = expanded === p.id;
            return (
              <React.Fragment key={p.id}>
                <tr
                  className="linked"
                  onClick={() => setExpanded(open ? null : p.id)}
                >
                  <td className="c-idx">{i + 1}</td>
                  <td className="c-name">{p.title}</td>
                  <td className="c-desc hide-sm">{p.shortDescription}</td>
                  <td className="c-area">{categoryLabel(p.category)}</td>
                  <td className="c-toggle">
                    <button
                      aria-expanded={open}
                      aria-label={p.title}
                      onClick={(e) => {
                        e.stopPropagation();
                        setExpanded(open ? null : p.id);
                      }}
                    >
                      {open ? "−" : "+"}
                    </button>
                  </td>
                </tr>
                {open && (
                  <tr className="row-detail">
                    <td colSpan={5}>
                      <div className="inner">
                        <img
                          className="shot"
                          src={`${process.env.PUBLIC_URL}${p.image}`}
                          alt={p.title}
                          loading="lazy"
                          decoding="async"
                        />
                        <div>
                          <p>{p.fullDescription}</p>
                          <div className="label tech-label">
                            {t("projects.technologies")}
                          </div>
                          <div className="tech">
                            {p.technologies.map((tech) => (
                              <span key={tech}>{tech}</span>
                            ))}
                          </div>
                          <div className="btn-row detail-links">
                            {p.links.live && (
                              <a
                                className="btn"
                                href={p.links.live}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {t("projects.visitSite")}
                              </a>
                            )}
                            {p.links.github && (
                              <a
                                className="btn"
                                href={p.links.github}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {t("projects.viewCode")}
                              </a>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>

      <div className="band-bottom" />
    </div>
  );
}

export default Projects;
